import get from 'lodash-es/get';
import { ApolloError } from 'apollo-client';
import { ValidationResult } from './validators';

export interface FormErrors<Message = string> {
  [path: string]: ValidationResult | Message | string;
}

interface BackendError<Message> {
  message: Message | string;
  path: string;
  details?: object;
}

interface TypedError {
  type: string;
  details?: {
    message: string;
    attributes?: any;
  };
}

interface NetworkError<T> {
  response: {
    data: T;
  };
}

export class ErrorDescription<Message extends string = string> {
  message: Message | string = 'Unknown error';
  details?: object;

  constructor(error?: ErrorDescription) {
    if (!error) {
      return;
    }

    this.message = error.message.replace(/:/g, '');
    this.details = error.details;

    if (error.message.startsWith('GraphQL error: ')) {
      this.message = error.message.replace('GraphQL error: ', '');
    }
  }
}

export const selectErrors = <Message extends string = string>(
  error: ApolloError
): BackendError<Message>[] =>
  get(error, 'graphQLErrors[0].extensions.exception.errors') ||
  get(error, 'graphQLErrors[0].extensions.errors') ||
  get(error, 'graphQLErrors') ||
  [];

export const getFormErrors = <Message extends string = string>(
  error: ApolloError | Error | string,
  // match fieldName from response to fieldName in form
  // if returns undefined fieldName becomes "_error"
  fieldMatcher: (fieldName: string) => string | undefined = (path) => path
): FormErrors<Message> | undefined => {
  const message = get(error, 'response.data.details.message');

  if (message) {
    const typedError = error as unknown as NetworkError<TypedError>;
    return {
      _error: new ErrorDescription({
        message: typedError.response.data.type.split('.').pop()!,
        details: typedError.response.data.details?.attributes,
      }),
    };
  }

  if (error instanceof ApolloError) {
    const gqlErrors = selectErrors(error);
    if (!gqlErrors || !gqlErrors.length) {
      return { _error: new ErrorDescription(error) };
    }
    if (!gqlErrors.find((error) => !!error.path)) {
      return { _error: new ErrorDescription(gqlErrors[0]) };
    }
    return gqlErrors.reduce(
      (result, { path, ...error }) => ({
        ...result,
        [fieldMatcher(path) || '_error']: new ErrorDescription(error),
      }),
      {}
    );
  }

  if (error instanceof Error) {
    return { _error: new ErrorDescription(error) };
  }

  return { _error: new ErrorDescription() };
};

export const formatApolloError = <Message extends string = string>(
  error?: ApolloError
): ErrorDescription | undefined => {
  if (!error) {
    return;
  }
  const gqlErrors = selectErrors(error);

  if (!gqlErrors || !gqlErrors.length) {
    return new ErrorDescription(error);
  }
  return new ErrorDescription(gqlErrors[0]);
};
